import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getAccessToken } from "features/common/utils/parseJwt";
import { downloadPDF, fetchPDF } from "features/common/utils/downloadPdf";
import { useUserGrantsContext } from "features/user/hooks/useUserGrantsContext";
import { checkOperationPermission } from "features/auth/utils/checkOperationPermission";

export interface useReportPreviewArgs {
  action: "RECONCILIATION" | "STATISTICS";
}

export const useReportPreview = ({ action }: useReportPreviewArgs) => {
  const [reportUrl, setReportUrl] = useState("");
  const [isFetchingReport, setIsFetchingReport] = useState(false);
  const [reportError, setReportError] = useState("");
  const [downloadSuccess, setDownloadSuccess] = useState<undefined | boolean>();
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const [downloadReportError, setDownloadReportError] = useState("");

  const { id } = useParams();
  const navigate = useNavigate();
  const token = getAccessToken();
  const { userGrants } = useUserGrantsContext();

  const downloadIsAllowed = useMemo(() => {
    let isPermitted = false;

    if (userGrants) {
      isPermitted = checkOperationPermission({
        operation: "E",
        component: "Download",
        //action === "RECONCILIATION" ? "Download Recon" : "Download Stat",
        func: "DOWNLOAD", //action === "RECONCILIATION" ? "DOWNLOAD_RECON" : "DOWNLOAD_STAT",
        userGrants,
      });
    }

    return isPermitted;
  }, [userGrants]);

  const getReport = useCallback(() => {
    if (id && token) {
      setReportError("");
      setIsFetchingReport(true);

      fetchPDF(id)
        .then((blob) => {
          setIsFetchingReport(false);
          if (!blob) return;
          if (blob instanceof Blob && blob.size > 0) {
            const blobObj = new Blob([blob], { type: "application/pdf" });
            const blobUrl = URL.createObjectURL(blobObj);
            setReportUrl(blobUrl);
          } else {
            setReportError("Invalid PDF data received");
          }
        })
        .catch((error) => {
          setIsFetchingReport(false);
          setReportError(error?.body?.message || error?.message);
        });
    }
  }, [id, token]);

  useEffect(() => {
    getReport();
  }, [getReport]);

  const handleDownloadReport = useCallback(() => {
    setIsDownloadingReport(true);

    if (id && token) {
      setIsDownloadingReport(true);
      downloadPDF(id)
        .then(() => {
          setIsDownloadingReport(false);
          setDownloadSuccess(true);
        })
        .catch((error) => {
          setIsDownloadingReport(false);
          setDownloadSuccess(false);
          setDownloadReportError(error?.body?.message || error?.message);
        });
    }
  }, [id, token]);

  const handleBack = () => navigate("/");

  return {
    reportUrl,
    downloadSuccess,
    handleDownloadReport,
    handleBack,
    isFetchingReport,
    reportError,
    isDownloadingReport,
    downloadReportError,
    downloadIsAllowed,
  };
};
